// Google Places lookups for the business listing and its reviews.
//
// Tries the current Places library first and falls back to the legacy
// PlacesService, so it works whichever of "Places API (New)" / "Places API"
// is switched on for the Maps key. Errors carry Google's own message so a
// misconfigured key says so instead of looking like "no results".

import { loadGoogleMaps } from "./googleMaps";

// Southwest Florida, so "Birdie Bus" finds this business, not a namesake.
const SWFL = { lat: 26.4, lng: -81.8 };
const BIAS_RADIUS_M = 50000;

function errorText(e) {
  return (e && (e.message || e.code || String(e))) || "unknown error";
}

/** Businesses matching `query`: [{ id, name, address, rating, total }]. */
export async function searchBusiness(query) {
  const google = await loadGoogleMaps();
  const failures = [];

  try {
    const { Place } = await google.maps.importLibrary("places");
    const { places } = await Place.searchByText({
      textQuery: query,
      fields: ["id", "displayName", "formattedAddress", "rating", "userRatingCount"],
      locationBias: { center: SWFL, radius: BIAS_RADIUS_M },
      maxResultCount: 8,
    });
    return (places || []).map((p) => ({
      id: p.id,
      name: p.displayName,
      address: p.formattedAddress,
      rating: p.rating,
      total: p.userRatingCount,
    }));
  } catch (e) {
    failures.push(`Places API (New): ${errorText(e)}`);
  }

  try {
    const svc = new google.maps.places.PlacesService(document.createElement("div"));
    const results = await new Promise((resolve, reject) =>
      svc.textSearch(
        { query, location: new google.maps.LatLng(SWFL.lat, SWFL.lng), radius: BIAS_RADIUS_M },
        (res, status) => {
          const S = google.maps.places.PlacesServiceStatus;
          if (status === S.OK) resolve(res || []);
          else if (status === S.ZERO_RESULTS) resolve([]);
          else reject(new Error(status));
        }
      )
    );
    return results.slice(0, 8).map((r) => ({
      id: r.place_id,
      name: r.name,
      address: r.formatted_address,
      rating: r.rating,
      total: r.user_ratings_total,
    }));
  } catch (e) {
    failures.push(`Places API: ${errorText(e)}`);
  }

  throw new Error(failures.join(" · "));
}

/** The listing's rating and reviews (Google returns up to 5). */
export async function fetchPlaceDetails(placeId) {
  const google = await loadGoogleMaps();
  const failures = [];

  try {
    const { Place } = await google.maps.importLibrary("places");
    const place = new Place({ id: placeId });
    await place.fetchFields({ fields: ["displayName", "rating", "userRatingCount", "reviews", "googleMapsURI"] });
    return {
      name: place.displayName,
      rating: place.rating,
      total: place.userRatingCount,
      url: place.googleMapsURI,
      reviews: (place.reviews || []).map((r) => ({
        author: r.authorAttribution?.displayName || "Google user",
        rating: r.rating,
        when: r.relativePublishTimeDescription,
        time: r.publishTime ? new Date(r.publishTime).getTime() / 1000 : 0,
        text: typeof r.text === "string" ? r.text : r.text?.text || "",
      })),
    };
  } catch (e) {
    failures.push(`Places API (New): ${errorText(e)}`);
  }

  try {
    const svc = new google.maps.places.PlacesService(document.createElement("div"));
    return await new Promise((resolve, reject) =>
      svc.getDetails(
        { placeId, fields: ["name", "rating", "user_ratings_total", "reviews", "url"] },
        (place, status) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
            reject(new Error(status));
            return;
          }
          resolve({
            name: place.name,
            rating: place.rating,
            total: place.user_ratings_total,
            url: place.url,
            reviews: (place.reviews || []).map((r) => ({
              author: r.author_name,
              rating: r.rating,
              when: r.relative_time_description,
              time: r.time,
              text: r.text,
            })),
          });
        }
      )
    );
  } catch (e) {
    failures.push(`Places API: ${errorText(e)}`);
  }

  throw new Error(failures.join(" · "));
}
